import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionSheetController, AlertController, IonContent, NavController } from '@ionic/angular';
import { finalize } from 'rxjs/operators';
import { CustomerAndAddressHeader } from 'src/app/models/customer-address/customer-and-address-header';
import { PostRecommendationUpdate } from 'src/app/models/recommendation/post-recommendation-update';
import { RecommendationEquipmentEditNavigationState } from 'src/app/models/recommendation/recommendation-equipment-edit-navigation-state';
import { RecommendationEquipmentListNavigationState } from 'src/app/models/recommendation/recommendation-equipment-list-navigation-state';
import { RecommendationEquipmentViewNavigationState } from 'src/app/models/recommendation/recommendation-equipment-view-navigation-state';
import { RecommendationFileViewNavigationState } from 'src/app/models/recommendation/recommendation-file-view-navigation-state';
import { ResponseStatusLevel } from 'src/app/models/recommendation/response-status-level';
import { ReturnRecommendation } from 'src/app/models/recommendation/return-recommendation';
import { ReturnRecommendationFile } from 'src/app/models/recommendation/return-recommendation-file';
import { ReturnRecommendationHistory } from 'src/app/models/recommendation/return-recommendation-history';
import { SearchResultQuote } from 'src/app/models/search/search-result-quote';
import { RecommendationUserDefinedStatusesAutocompleteDataLoaderService } from 'src/app/services/autocomplete-data-loader/recommendation-user-defined-statuses-autocomplete-data-loader.service';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { PermissionCheckerService } from 'src/app/services/permission-checker/permission-checker.service';
import { RecommendationService } from 'src/app/services/recommendation/recommendation.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { UserDataService } from 'src/app/services/user-data/user-data.service';
import { UtilityService } from 'src/app/services/utility.service';
import { XoiService } from 'src/app/services/xoi.service';
import { Platform } from '@ionic/angular';
import { RecommendationEquipmentAddNavigationState } from 'src/app/models/recommendation/recommendation-equipment-add-navigation-state';
import { EquipmentTypeEnum } from 'src/app/models/equipment/equipment-type-enum';
import { RecommendationItemListNavigationState } from 'src/app/models/recommendation/recommendation-item-list-navigation-state';
import { forkJoin } from 'rxjs';
import { ReturnMessage } from 'src/app/models/return-message';
import { ReturnRecommendationPayload } from 'src/app/models/recommendation/return-recommendation-payload';
import { ReturnCount } from 'src/app/models/recommendation/return-count';
import { ReturnRecommendationFilePayload } from 'src/app/models/recommendation/return-recommendation-file-payload';
import { FileImageUploadNavigationState } from 'src/app/models/shared/file-image-upload-navigation-state';
import { RecommendationFileAudioUploadNavigationState } from 'src/app/models/recommendation/recommendation-file-audio-upload-navigation-state';
import { QuoteAddNavigationState } from 'src/app/models/quote/quote-add-navigation-state';
import { QuoteEditNavigationState } from 'src/app/models/quote/quote-edit-navigation-state';
import { GenericAddStepOneModelEnum } from '../../generic-add-step-one/generic-add-step-one-model-enum';
import { CopyRecommendationNavigationState } from 'src/app/models/recommendation/copy-recommendation-navigation-state';
import { RecommendationFileUploadNavigationState } from 'src/app/models/recommendation/recommendation-file-upload-navigation-state';
import { RecommendationQuestionsNavigationState } from 'src/app/models/recommendation/recommendation-questions-navigation-state';
import { Title } from '@angular/platform-browser';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { ResponseRegions } from 'src/app/models/shared/response-regions';


@Component({
  selector: 'app-recommendation-edit',
  templateUrl: './recommendation-edit.page.html',
  styleUrls: ['./recommendation-edit.page.scss', '../../../shared-styles/upload-media-buttons.scss'],
})
export class RecommendationEditPage implements OnInit {

  @ViewChild('content') content: IonContent;

  title: string = '';
  header: CustomerAndAddressHeader = null;
  history: ReturnRecommendationHistory[];
  itemCount: number;
  associatedQuotes: SearchResultQuote[];
  statusOfRecommendation: ResponseStatusLevel;
  files: ReturnRecommendationFile[];
  recommendation: ReturnRecommendation = null;
  form: FormGroup;
  canCRUDRecommendations: boolean;
  canUpdateCustomStatus: boolean;
  canCopyRecommendations: boolean;
  canCreateQuote: boolean;
  customerAddressHasEquipments: boolean;
  isMobilePlatform: boolean;
  regions: ResponseRegions;
  isReadOnly: boolean;
  canCRUDEquipments: boolean;
  canEmailCustomerQuote: boolean;
  recommendationId: number;

  validation_messages = {
    'description': [
      { description: 'required', message: 'Description is required.' },
      { description: 'maxlength', message: 'Description max length is 30.' },
    ]
  };

  constructor(private router: Router,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private navController: NavController,
    private titleService: Title,
    private platform: Platform,
    private alertCtrl: AlertController,
    private actionSheetCtrl: ActionSheetController,
    private loadingWrapper: LoadingControllerWrapperService,
    private toastWrapper: ToastControllerWrapperService,
    private recommendationService: RecommendationService,
    private permissionChecker: PermissionCheckerService,
    private userDataService: UserDataService,
    private utilitiesService: UtilityService,
    private xoiService: XoiService,
    public userStatusesDataLoader: RecommendationUserDefinedStatusesAutocompleteDataLoaderService,
  ) {
    this.statusOfRecommendation = <ResponseStatusLevel>{ statusLevel: -1 };
  }

  async ngOnInit() {
    this.form = this.fb.group({
      description: [null, [Validators.required, Validators.maxLength(30)]],
      details: [''],
      benefits: [''],
      userDefinedStatus: [null],
      regionId: [''],
    });
    this.isMobilePlatform = this.userDataService.isMobilePlatform();
    this.canCRUDRecommendations = await this.permissionChecker.canCRUDRecommendations();
    this.canUpdateCustomStatus = this.canCRUDRecommendations;
    this.canCopyRecommendations = this.canCRUDRecommendations;
    this.isReadOnly = await this.permissionChecker.isReadOnly();

    this.recommendationId = Number(this.route.snapshot.paramMap.get('recommendationId'));
    if (!this.recommendationId)
    {
      this.navController.navigateRoot(['']);
    }
    else
    {
      this.title = `Edit Recommendation #${this.recommendationId}`;
      this.titleService.setTitle(this.title);
    }
  }

  async ionViewDidEnter() {
    if (!this.recommendationId) {
      return;
    }

    await this.loadingWrapper.presentLoading('');

    forkJoin([
      this.permissionChecker.canCRUDRecommendations(),
      this.permissionChecker.canCRUDEquipments(),
      this.recommendationService.getStatusOfRecommendation(this.recommendationId),
      this.recommendationService.get(this.recommendationId),
      this.recommendationService.getItemsCount(this.recommendationId),
      this.recommendationService.getAssociatedQuotes(this.recommendationId),
      this.recommendationService.getHistory(this.recommendationId),
      this.recommendationService.getFiles(this.recommendationId),
      this.route.snapshot.data.regions
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([canCrudRec, canCrudEquip, getStatusOfRecommendation, get, getItemsCount, getAssociatedQuotes, getHistory, getFiles, regions]:
        [boolean,
          boolean,
          ReturnMessage<ResponseStatusLevel>,
          ReturnMessage<ReturnRecommendationPayload>,
          ReturnMessage<ReturnCount>,
          ReturnMessage<SearchResultQuote[]>,
          ReturnMessage<ReturnRecommendationHistory[]>,
          ReturnMessage<ReturnRecommendationFilePayload>,
          ResponseRegions]) => {
        this.canCRUDRecommendations = canCrudRec;
        this.canCRUDEquipments = canCrudEquip;
        this.statusOfRecommendation = getStatusOfRecommendation.success ? getStatusOfRecommendation.payload : null;
        this.recommendation = get.success ? get.payload.recommendation : null;
        
        if (this.recommendation) {
          this.header = this.recommendation.customerAndAddressHeader;
          this.form.patchValue({
            description: this.recommendation.description,
            details: this.recommendation.details ? this.recommendation.details : '',
            benefits: this.recommendation.benefits ? this.recommendation.benefits : '',
            userDefinedStatus: this.recommendation.userDefinedStatusId ? { id: this.recommendation.userDefinedStatusId, description: this.recommendation.userDefinedStatus } : null
          });

          if (this.recommendation.readOnly) {
            this.canCRUDRecommendations = false;
          }
          this.canEmailCustomerQuote = this.recommendation.canEmailCustomerQuote;
          this.customerAddressHasEquipments = this.recommendation.customerAddressHasEquipments;
        }

        this.itemCount = getItemsCount.success ? getItemsCount.payload.count : null;
        this.associatedQuotes = getAssociatedQuotes.success ? getAssociatedQuotes.payload : [];
        this.history = getHistory.success ? getHistory.payload : null;
        this.files = getFiles.success ? getFiles.payload.files : [];
        this.regions = regions;
        if (this.recommendation && this.recommendation.regionId) {
          this.form.get('regionId').setValue(this.recommendation.regionId.toString(), { emitEvent: false });
        }

        this.content.scrollToTop();
      },
        err => {
          //notify the user and kick them back out to dashboard
          this.toastWrapper.presentErrorToast(err.error?.message ?? err.error);
          this.navController.navigateRoot(['']);
        });
  }

  async submit() {
    if (this.form.valid) {
      await this.loadingWrapper.presentLoading('');

      var post = <PostRecommendationUpdate>{
        id: this.recommendation.id,
        description: this.form.value.description,
        benefits: this.form.value.benefits,
        details: this.form.value.details,
        userDefinedStatusId: this.form.value.userDefinedStatus ? this.form.value.userDefinedStatus.id : null,
        regionId: this.form.value.regionId != "0" ? this.form.value.regionId : null
      };

      this.recommendationService.update(post)
        .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
        .subscribe(response => {
          if (response.success) {
            this.recommendationService.getHistory(this.recommendationId)
              .subscribe(response => response.success ? this.history = response.payload : null);
          }
        });
    }
    else {
      this.form.markAllAsTouched();
      await this.toastWrapper.presentErrorToast('Please complete the form');
    }
  }

  goToEquipmentAdd() {
    if (this.customerAddressHasEquipments) {
      this.router.navigate(['equipment-list'], { // show list of available equipments
        relativeTo: this.route,
        state: <RecommendationEquipmentListNavigationState>{
          recommendationId: this.recommendationId,
          addressId: this.recommendation.addressId,
          addressType: this.recommendation.addressType
        }
      });
    }
    else {
      if (this.canCRUDEquipments) {
        this.router.navigate(['equipment'], { // add a new equipment
          relativeTo: this.route,
          state: <RecommendationEquipmentAddNavigationState>{
            recommendationId: this.recommendationId,
            addressId: this.recommendation.addressId,
            addressType: this.recommendation.addressType
          }
        });
      }
      else {
        this.toastWrapper.presentErrorToast('The address contains no equipments.');
      }
    }
  }

  goToEquipmentEdit() {
    if (this.canCRUDRecommendations) {
    
    let canEditEquipment = this.recommendation.equipmentType === EquipmentTypeEnum.RECOMMENDATION_EQUIPMENT || this.recommendation.equipmentType === EquipmentTypeEnum.UPDATE_EQUIPMENT;
    if (canEditEquipment) {

        this.router.navigate(['equipment', this.recommendation.equipmentId],
          {
            relativeTo: this.route,
            state: <RecommendationEquipmentEditNavigationState>{
              recommendationId: this.recommendation.id,
              equipmentType: this.recommendation.equipmentType,
              equipmentId: this.recommendation.equipmentId,
              addressId: this.recommendation.addressId,
              addressType: this.recommendation.addressType,
              isGpEquipmentPendingChanges: this.recommendation.isGpEquipmentPendingChange
            }
          });
      }
      else {
        this.router.navigate(['equipment-view', this.recommendation.equipmentId],
          {
            relativeTo: this.route,
            state: <RecommendationEquipmentViewNavigationState>{
              recommendationId: this.recommendation.id,
              equipmentType: this.recommendation.equipmentType,
              equipmentId: this.recommendation.equipmentId,
              addressId: this.recommendation.addressId,
              addressType: this.recommendation.addressType,
              isGpEquipmentPendingChange: false
            }
          });
      }

    }
    else {
      this.toastWrapper.presentErrorToast('You do not have permission to edit equipment');
    }
  }

  goToAddItems() {
    this.router.navigate(['items'], {
      relativeTo: this.route,
      state: <RecommendationItemListNavigationState>{
        recommendationId: this.recommendation.id,
        addressId: this.recommendation.addressId,
        addressType: this.recommendation.addressType
      }
    });
  }

  goToQuote(quote: SearchResultQuote) {
    this.router.navigate(['quote', quote.id], {
      state: <QuoteEditNavigationState>{
        quoteId: quote.id
      }
    })
  }

  goToRecommendationQuestions() {
    this.router.navigate(['questions'], {
      relativeTo: this.route,
      state: <RecommendationQuestionsNavigationState>{
        recommendationId: this.recommendationId
      }
    });
  }

  notifyOffice() {
    this.recommendationService.notifyOffice(this.recommendation.id)
      .subscribe(response => {
        this.toastWrapper.display('The email has been sent.');
        this.recommendationService.getHistory(this.recommendation.id)
          .subscribe(response => this.history = response.payload);
      }
      );
  }

  async nofityByEmail() {
    const prompt = await this.alertCtrl.create({
      header: 'Send Email',
      message: "Please Provide an Email",
      inputs: [
        {
          name: 'email',
          placeholder: 'example@email.com',
          value: this.recommendation.email
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: data => {
            console.log('Cancel');
          }
        },
        {
          text: 'Send Email',
          handler: data => {
            this.sendEmailWithCustomerEmail(data);
          }
        }
      ]
    });
    await prompt.present();
  }

  sendEmailWithCustomerEmail(data) {
    var emailAddressFieldValidation = this.utilitiesService.validateEmailAddressField(data.email);
    if (!emailAddressFieldValidation.validEmailAddressField) {
      this.toastWrapper.presentErrorToast(emailAddressFieldValidation.errorMessage);
    }
    else {
      this.recommendationService.notifyCustomerWithCustomerEmail(this.recommendation.id, data).subscribe(
        data => {
          this.toastWrapper.display('The email has been sent.');
          this.recommendationService.getHistory(this.recommendation.id)
            .subscribe(data => this.history = data.payload);
        }
      );
    }
  }

  createQuote() {
    this.router.navigate(['quote'], {
      state: <QuoteAddNavigationState>{
        recommendationId: this.recommendationId,
        addressId: this.recommendation.addressId,
        addressType: this.recommendation.addressType
      }
    })
  }

  goToFileAudioUpload() {
    this.router.navigate(['audio-upload'], {
      relativeTo: this.route,
      state: <RecommendationFileAudioUploadNavigationState>{
        recommendationId: this.recommendationId,
        existingFileNames: this.files.map(f => f.name)
      }
    });
  }

  goToFileImageUpload() {
    this.router.navigate(['image-upload'], {
      relativeTo: this.route,
      state: <FileImageUploadNavigationState>{ 
        recommendationId: this.recommendationId,
        existingFileNames: this.files.map(f => f.name)
      },
    });
  }

  goToFileUpload() {
    this.router.navigate(['file-upload'], {
      relativeTo: this.route,
      state: <RecommendationFileUploadNavigationState>{
        recommendationId: this.recommendationId
      }
    });
  }

  async presentFileActionSheet(uploadedFile) {
    var buttons = this.getFileActionSheetOptions(uploadedFile);
    const actionSheet = await this.actionSheetCtrl.create({
      header: uploadedFile.name + " - " + uploadedFile.createdDate,
      buttons: buttons
    });
    await actionSheet.present();
  }

  getFileActionSheetOptions(uploadedFile: ReturnRecommendationFile) {
    var buttonsForSheet = [];
    buttonsForSheet.push(
      {
        text: 'View',
        icon: 'eye',
        handler: () => {
          this.router.navigate(['file-view', uploadedFile.id], {
            relativeTo: this.route,
            state: <RecommendationFileViewNavigationState>{
              file: uploadedFile,
              recommendation: this.recommendation,
              header: this.header
            }
          });
        }
      }
    );
    if (this.recommendation.allowFileDeletion && this.isReadOnly == false) {
      buttonsForSheet.push(
        {
          text: 'Delete',
          icon: 'trash',
          handler: () => {
            this.recommendationService.deleteFile(uploadedFile.id).subscribe(
              response => {
                if (response.success) {
                  this.recommendationService.getFiles(this.recommendationId)
                    .subscribe(response => response.success ? this.files = response.payload.files : []);
                }
              }
            );
          }
        }
      );
    }
    buttonsForSheet.push(
      {
        text: 'Cancel',
        role: 'cancel',
        icon: 'close'
      }
    );
    return buttonsForSheet;
  }

  async createXoiJob() {
    await this.loadingWrapper.presentLoading('');
    this.xoiService.createXoiJobAndJobShare(this.recommendationId)
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(
        response => {
          if (response.success) {
            this.recommendation.xoiJobId = response.payload.xoiJobInfo.JobId;
            this.recommendation.xoiShareLink = response.payload.xoiJobInfo.ShareLink;
            this.recommendation.xoiJobDetails = response.payload.xoiJobInfo.JobDetails;
            this.toastWrapper.display("XOi Vision job created.");
          }
        }
      );
  }

  launchXoi() {
    if (this.isMobilePlatform) {
      this.launchExternalApp();
    }
    else {
      window.open(this.recommendation.xoiShareLink, '_blank');
    }
  }

  launchExternalApp() {

    if (this.platform.is('android') || this.platform.is('ios')) {
      var xoiShareLink = this.recommendation.xoiShareLink;
      var jsonObj = {
        "jobId": this.recommendation.xoiJobId,
        "owner": this.recommendation.xoiJobDetails["assigneeId"],
        "jobLocation": this.recommendation.xoiJobDetails["jobLocation"],
        "customerName": this.recommendation.xoiJobDetails["customerName"],
        "workOrderNumber": this.recommendation.xoiJobDetails["workOrderNumber"],
        "supportsMultipleWorkflows": true
      };

      var jsonObjStringified = JSON.stringify(jsonObj);
      console.log("Deeplink JSON payload:", jsonObjStringified);
      var jsonEncodedXoiJobString = encodeURI(jsonObjStringified);
      var uri = "xoi-vision://my-work/edit?payload=" + jsonEncodedXoiJobString
      var packageName =  'io.xoi.vision.ea';

      console.log('Starting/loading XOi Vision job page.');
      AppLauncher.openUrl({ url: uri }).catch(function(err) {
        // If the deeplink failed then open the browser
        console.log('Error starting/loading XOi Vision job page. ' + err);
        console.log('Starting/loading browser job page.');
        Browser.open({ url: xoiShareLink }).catch(function(err2) {
          console.log('Error starting/loading browser job page. ' + err2);
        });
      });
    }
    else {
      //we only support android and ios. If any other os, then load the web url.
      window.open(this.recommendation.xoiShareLink, '_blank');
    }

  }

  trackById(index: number, item: any): number {
    return item.id;
  }

  copyRecommendation() {
    this.router.navigate(['select-customer', GenericAddStepOneModelEnum.RECOMMENDATION_COPY], {
      state: <CopyRecommendationNavigationState>{
        srcRecommendationId: this.recommendationId
      }
    });
  }

  regionUpdated(event) {
    if (event.target.value == 0) {
      this.form.get('regionId').setValue('');
    }
  }
}
